import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Reusable iframe wrapper that renders email-template HTML at its natural
 * 640px width, then CSS-transform-scales to fit any container width.
 *
 * Why a transform: iOS Safari ignores `<meta name="viewport">` inside an
 * srcDoc iframe, so a desktop-width email overflows on mobile if we don't
 * manually scale it down. Using `transform: scale(...)` is the workaround
 * that's actually battle-tested across browsers.
 *
 * We also auto-size the iframe to its content so the inner page never has
 * its own scrollbars — the parent page scrolls as a single document.
 *
 * IMPORTANT iOS regression note: index.css has a global safety rule
 * `iframe { max-width: 100% }` to stop runaway embeds on phones. That
 * rule was capping THIS iframe at the wrapper's clientWidth instead of
 * letting it render at 640px. Combined with transform: scale, the email
 * was being scaled DOWN twice and ended up ~216px wide on a 390px iPhone,
 * with a giant empty strip on the right. We override max-width: none and
 * width: 640 directly so the iframe always renders at its natural width
 * BEFORE the transform compresses it to fit. See `wrapperRef` below — we
 * use a ref + ResizeObserver so the very first paint already has the
 * correct scale (no flash of full-size content).
 *
 * Props:
 *   html      — the full HTML string to render (server-rendered email).
 *   wrapperId — DOM id used to read the wrapper width on resize. Pass a
 *               unique value if you mount multiple EmailFrames on the
 *               same page; defaults to "wv-email-frame-wrapper".
 *   minHeight — optional fallback height while the content loads.
 */
export default function EmailFrame({ html, wrapperId = "wv-email-frame-wrapper", minHeight = 800 }) {
  const wrapperRef = useRef(null);
  const iframeRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(minHeight);

  // Synchronously measure & set scale BEFORE the browser paints the first
  // frame — eliminates the brief "iframe at 640px" flash that previously
  // caused horizontal overflow on initial mount on phones.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const NATURAL = 640;
    const w = wrapper.clientWidth || window.innerWidth;
    setScale(Math.min(1, w / NATURAL));
  }, [html]);

  // Track wrapper resize (orientation, dynamic layout shifts, sidebar
  // appearing, etc) using ResizeObserver — more reliable than
  // window.resize because the wrapper width can change without the
  // window changing (e.g. parent flex container reflow).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const NATURAL = 640;
    const recompute = () => {
      const w = wrapper.clientWidth || window.innerWidth;
      setScale(Math.min(1, w / NATURAL));
    };
    recompute();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(recompute);
      ro.observe(wrapper);
    }
    window.addEventListener("orientationchange", recompute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("orientationchange", recompute);
    };
  }, [html, wrapperId]);

  // After srcDoc loads, measure inner content height so the wrapper sizes
  // perfectly and the page scrolls as one document.
  //
  // We use body.scrollHeight EXCLUSIVELY (not documentElement.scrollHeight)
  // because of a self-feedback loop: once we set the iframe's `height`
  // attribute, the contentDocument's <html> element absorbs that height
  // — so `documentElement.scrollHeight` reports the iframe's CURRENT
  // height, not the actual content height. Taking `Math.max` of the two
  // therefore latches the wrapper to whatever the largest measurement
  // ever was, leaving phantom white space at the bottom (a 129px
  // overhang in the bug report from May 2026).
  // body.scrollHeight is grounded in the actual rendered content, so
  // shrinkage caused by font scaling / image-load reflow is reflected
  // correctly.
  const handleIframeLoad = () => {
    try {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentDocument) return;
      const doc = iframe.contentDocument;
      const measure = () => {
        const h = doc.body?.scrollHeight || 0;
        if (h > 0) setContentHeight(h);
      };
      measure();
      const imgs = doc.images || [];
      Array.from(imgs).forEach((img) => {
        if (!img.complete) img.addEventListener("load", measure, { once: true });
      });
      if (doc.fonts && doc.fonts.ready) {
        doc.fonts.ready.then(measure).catch(() => {});
      }
      setTimeout(measure, 600);
      setTimeout(measure, 1500);
    } catch {
      // cross-origin or doc not ready — fine to ignore.
    }
  };

  // Scale-aware font-size injection.
  //
  // The iframe renders its email at natural 640px, then gets visually
  // shrunk by `transform: scale(scale)`. A 16px font naturally rendered
  // ends up at `16 * scale` on screen — on a 390px phone that's ~9.3px,
  // which the user reads as "tiny body text". To make text land at a
  // chosen ON-SCREEN size regardless of viewport, we inject font-sizes
  // INVERSELY scaled inside the iframe: `target / scale`. After the
  // visual scale is applied, they render at exactly `target` px to the
  // eye — matching the surrounding React UI on the dashboard.
  //
  // Only runs once the iframe document is alive AND on every scale
  // change (orientation, resize, parent reflow). The TARGETS below
  // reference the dashboard's surrounding type scale: 15px body to
  // pair with the React subtitle ("Your weather and news…"), 11px
  // tags, 30px H1, etc.
  //
  // Side effect: re-flowing font sizes changes contentHeight, so we
  // call measure() again right after injecting.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return;
    const doc = iframe.contentDocument;
    if (!doc.head) return;
    // Target ON-SCREEN sizes (post-scale). Tuned to feel proportional
    // to the React UI around the iframe — body matches the dashboard
    // subtitle so the boundary between iframe and outer page is
    // invisible to the eye.
    const TARGETS = {
      body: 15,        // wv-body-copy / wv-narrative
      tag: 11,         // wv-tag
      caption: 12,     // wv-caption
      headline: 20,    // wv-headline-sm
      h1: 28,          // wv-h1
      h2: 20,          // wv-h2
      highLow: 34,     // wv-high-low
    };
    const inv = scale > 0 ? 1 / scale : 1;
    const px = (n) => `${Math.round(n * inv)}px`;
    const css = `
      .wv-body-copy, .wv-body-copy * { font-size: ${px(TARGETS.body)} !important; line-height: 1.55 !important; }
      .wv-narrative, .wv-narrative * { font-size: ${px(TARGETS.body)} !important; line-height: 1.55 !important; }
      .wv-tag { font-size: ${px(TARGETS.tag)} !important; }
      .wv-caption { font-size: ${px(TARGETS.caption)} !important; }
      .wv-headline-sm { font-size: ${px(TARGETS.headline)} !important; line-height: 1.2 !important; }
      .wv-h1 { font-size: ${px(TARGETS.h1)} !important; line-height: 1.1 !important; }
      .wv-h2 { font-size: ${px(TARGETS.h2)} !important; line-height: 1.15 !important; }
      .wv-high-low { font-size: ${px(TARGETS.highLow)} !important; }
    `;
    const STYLE_ID = "wv-emailframe-scale-overrides";
    let el = doc.getElementById(STYLE_ID);
    if (!el) {
      el = doc.createElement("style");
      el.id = STYLE_ID;
      doc.head.appendChild(el);
    }
    if (el.textContent !== css) {
      el.textContent = css;
      // Font reflow changes content height — remeasure on next tick so
      // the wrapper height matches. body.scrollHeight only (see
      // handleIframeLoad rationale).
      setTimeout(() => {
        const h = doc.body?.scrollHeight || 0;
        if (h > 0) setContentHeight(h);
      }, 50);
    }
  }, [scale, html, contentHeight]);

  return (
    <div
      id={wrapperId}
      ref={wrapperRef}
      style={{
        // Cap the wrapper at the iframe's natural 640px width so the
        // outer card border hugs the actual content. Without this, on
        // wide viewports (desktop, tablet) the wrapper would expand to
        // 100% of its parent while the iframe stayed 640px — leaving a
        // big strip of whitespace inside the bordered card. Now the
        // wrapper is exactly as wide as its content on every screen.
        width: "100%",
        maxWidth: 640,
        marginLeft: "auto",
        marginRight: "auto",
        height: contentHeight * scale,
        overflow: "hidden",
        position: "relative",
        // Defense in depth for iOS Safari: `contain: paint` enforces a
        // strict clipping boundary so the post-transform iframe can't
        // leak past the wrapper's box (a known WebKit quirk where the
        // pre-transform bounding box was used for some scroll calcs).
        contain: "paint",
      }}
    >
      <iframe
        ref={iframeRef}
        title="Weathervane email preview"
        srcDoc={html}
        onLoad={handleIframeLoad}
        scrolling="no"
        style={{
          border: 0,
          width: 640,
          // Critical: the global `iframe { max-width: 100% }` rule in
          // index.css (mobile safety net for runaway embeds) was
          // capping this iframe at the wrapper's clientWidth and then
          // transform: scale was shrinking it AGAIN — leaving the
          // email rendered at ~216px on a 390px phone with a huge
          // empty strip to the right. Forcing max-width: none lets
          // the iframe sit at its natural 640px so the scale below
          // is the ONLY width transform applied.
          maxWidth: "none",
          height: contentHeight,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          display: "block",
        }}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
      />
    </div>
  );
}
